import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaTrophy, FaHome, FaBars, FaTimes, FaFileAlt } from 'react-icons/fa';

const HeaderContainer = styled.header`
  background-color: ${({ theme }) => theme.colors.surface};
  padding: 1rem 2rem;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: all 0.3s ease-in-out;
  
  &.scrolled {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: bold;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1.2rem;
  }
`;

const DesktopNav = styled.div`
  display: none;
  align-items: center;
  gap: 2rem;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1rem;
  }
`;

const MobileMenuButton = styled.button`
  display: block;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
  background: ${({ theme }) => theme.colors.surface};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavLink = styled(NavLink)`
  padding: 1rem 2rem;
  border-radius: 0;
  border-left: 3px solid transparent;
  
  &.active {
    border-left-color: ${({ theme }) => theme.colors.accent};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <HeaderContainer className={scrolled ? 'scrolled' : ''}>
      <Nav>
        <Logo to="/">RaffleGame</Logo>
        
        <DesktopNav>
          <NavLink to="/" className={location.pathname === '/' ? 'active' : ''}>
            <FaHome /> Home
          </NavLink>
          <NavLink to="/winners" className={location.pathname === '/winners' ? 'active' : ''}>
            <FaTrophy /> Winners
          </NavLink>
          <NavLink to="/whitepaper" className={location.pathname === '/whitepaper' ? 'active' : ''}>
            <FaFileAlt /> Whitepaper
          </NavLink>
        </DesktopNav>
        
        <MobileMenuButton onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>
      </Nav>
      
      {isMobileMenuOpen && (
        <MobileMenu>
          <MobileNavLink to="/" onClick={closeMobileMenu} className={location.pathname === '/' ? 'active' : ''}>
            <FaHome /> Home
          </MobileNavLink>
          <MobileNavLink to="/winners" onClick={closeMobileMenu} className={location.pathname === '/winners' ? 'active' : ''}>
            <FaTrophy /> Winners
          </MobileNavLink>
          <MobileNavLink to="/whitepaper" onClick={closeMobileMenu} className={location.pathname === '/whitepaper' ? 'active' : ''}>
            <FaFileAlt /> Whitepaper
          </MobileNavLink>
        </MobileMenu>
      )}
    </HeaderContainer>
  );
};

export default Header;
